'use client';

import { Row } from '@slims/ui/atoms';
import { InlineNotice, LabeledSelect } from '@slims/ui/molecules';
import { useAdminScopeTree } from '@/hooks/use-admin-scope';
import Link from '@/lib/next/link';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

/**
 * Scope selector for Soci & Onboarding: one dropdown for the CER and one for
 * the configuration within it. Writes the admin scope store (independent from
 * the global view selector), which the admin pages send as ?orgId=.
 * Seeding/validation of the stored scope lives in useScopeSync (app shell).
 */
export function ConfigSwitcher() {
	const { tree: cers, loading } = useAdminScopeTree();
	const cerId = useAdminScopeStore((s) => s.cerId);
	const configId = useAdminScopeStore((s) => s.configId);
	const setCer = useAdminScopeStore((s) => s.setCer);
	const setConfig = useAdminScopeStore((s) => s.setConfig);

	// Still loading the first fetch — render nothing rather than flash an empty state.
	if (loading && cers.length === 0) return null;

	// No CER has a configuration to select — show why, instead of silently
	// hiding the selector and leaving the admin without a configuration scope.
	if (cers.length === 0) {
		return (
			<InlineNotice mb="md">
				Nessuna configurazione disponibile per le CER a cui hai accesso.
			</InlineNotice>
		);
	}

	const selectedCer = cers.find((c) => c.id === cerId) ?? cers[0];
	const configs = selectedCer?.configs ?? [];
	const activeConfigId = configs.some((c) => c.id === configId) ? (configId ?? '') : '';

	function handleCerChange(nextCerId: string) {
		const cer = cers.find((c) => c.id === nextCerId);
		if (!cer) return;
		// Switch to the CER's first configuration.
		setCer(cer.id, cer.configs[0]?.id ?? null);
	}

	function handleConfigChange(nextConfigId: string) {
		if (nextConfigId && nextConfigId !== configId) setConfig(nextConfigId);
	}

	return (
		<>
			<Row wrap gap="md" mb="md">
				<LabeledSelect label="CER" value={selectedCer?.id ?? ''} onChange={handleCerChange}>
					{cers.map((c) => (
						<option key={c.id} value={c.id}>
							{c.nome}
						</option>
					))}
				</LabeledSelect>
				<LabeledSelect
					label="Configurazione"
					value={activeConfigId}
					onChange={handleConfigChange}
					disabled={configs.length === 0}
				>
					{activeConfigId === '' && <option value="">- seleziona -</option>}
					{configs.map((c) => (
						<option key={c.id} value={c.id}>
							{c.nome}
						</option>
					))}
				</LabeledSelect>
			</Row>
			{selectedCer && configs.length === 0 && (
				<InlineNotice mb="md">
					{selectedCer.nome} non ha ancora una configurazione, e i soci appartengono a una
					configurazione: creane una in <Link href="/configurations">Configurazioni</Link>.
				</InlineNotice>
			)}
		</>
	);
}
